import { TablePagination, type PaginationState } from '@/components/shared/TablePagination';
import { Badge } from '@/components/ui-kit/badge';
import { Button } from '@/components/ui-kit/button';
import { Input } from '@/components/ui-kit/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui-kit/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui-kit/table';

import {
  exportTemplate,
  downloadTemplateJson,
} from '@/modules/admin/templates/lib/export-import.service';
import { Template } from '@/modules/templates/types/templates.types';
import type { LucideIcon } from 'lucide-react';
import {
  Briefcase,
  Building,
  ClipboardList,
  Download,
  FileCheck,
  FilePlus,
  FileSearch,
  FileText,
  Files,
  Folder,
  Handshake,
  Lock,
  MoreHorizontal,
  Pencil,
  Scale,
  Search,
  Settings2,
  Shield,
  Sparkles,
  Trash2,
  UserCheck,
  Users,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

// Map icon names to components
const iconMap: Record<string, LucideIcon> = {
  FileText,
  Users,
  Lock,
  Shield,
  Sparkles,
  FileCheck,
  FilePlus,
  FileSearch,
  Files,
  Folder,
  Scale,
  Briefcase,
  Building,
  Handshake,
  UserCheck,
  ClipboardList,
};

interface TemplateTableProps {
  templates: Template[];
  pagination: PaginationState;
  search: string;
  onSearchChange: (value: string) => void;
  onPageChange: (page: number) => void;
  onDelete: (template: Template) => void;
  loading?: boolean;
}

export function TemplateTable({
  templates,
  pagination,
  search,
  onSearchChange,
  onPageChange,
  onDelete,
  loading,
}: TemplateTableProps) {
  const locale = useTranslation().i18n.language;
  const { t } = useTranslation();

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-globe-grey" />
        <Input
          placeholder={t('ADMIN_TEMPLATES_TABLE_SEARCHPLACEHOLDER')}
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Table */}
      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted">
              <TableHead className="font-semibold text-[hsl(var(--fg))]">
                {t('ADMIN_TEMPLATES_TABLE_COLUMNS_TEMPLATE')}
              </TableHead>
              <TableHead className="font-semibold text-[hsl(var(--fg))]">
                {t('ADMIN_TEMPLATES_TABLE_COLUMNS_SLUG')}
              </TableHead>
              <TableHead className="font-semibold text-[hsl(var(--fg))]">
                {t('ADMIN_TEMPLATES_TABLE_COLUMNS_STATUS')}
              </TableHead>
              <TableHead className="font-semibold text-[hsl(var(--fg))]">
                {t('ADMIN_TEMPLATES_TABLE_COLUMNS_POPULAR')}
              </TableHead>
              <TableHead className="font-semibold text-[hsl(var(--fg))]">Type</TableHead>
              <TableHead className="font-semibold text-[hsl(var(--fg))]">
                {t('ADMIN_TEMPLATES_TABLE_COLUMNS_UPDATED')}
              </TableHead>
              <TableHead className="text-right font-semibold text-[hsl(var(--fg))]">
                {t('ADMIN_TEMPLATES_TABLE_COLUMNS_ACTIONS')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              [...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="size-10 rounded-lg bg-muted animate-pulse" />
                      <div className="space-y-2">
                        <div className="h-4 w-32 bg-muted rounded animate-pulse" />
                        <div className="h-3 w-48 bg-muted rounded animate-pulse" />
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="h-4 w-24 bg-muted rounded animate-pulse" />
                  </TableCell>
                  <TableCell>
                    <div className="h-5 w-16 bg-muted rounded-full animate-pulse" />
                  </TableCell>
                  <TableCell>
                    <div className="h-5 w-16 bg-muted rounded-full animate-pulse" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 w-20 bg-muted rounded animate-pulse" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 w-8 bg-muted rounded animate-pulse ml-auto" />
                  </TableCell>
                </TableRow>
              ))
            ) : templates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="h-24 text-center text-globe-grey">
                  {search
                    ? t('ADMIN_TEMPLATES_TABLE_EMPTY_SEARCH')
                    : t('ADMIN_TEMPLATES_TABLE_EMPTY_DEFAULT')}
                </TableCell>
              </TableRow>
            ) : (
              templates.map((template) => {
                const IconComponent = iconMap[template?.icon || 'FileText'] || FileText;
                return (
                  <TableRow key={template.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="flex size-10 items-center justify-center rounded-lg bg-selise-blue/10">
                          <IconComponent className="size-5 text-selise-blue" />
                        </div>
                        <div>
                          <p className="font-medium text-[hsl(var(--fg))]">{template.title}</p>
                          <p className="text-sm text-globe-grey line-clamp-1 max-w-xs">
                            {template.description}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <code className="text-sm bg-muted px-2 py-1 rounded text-[hsl(var(--fg))]">
                        {template.slug}
                      </code>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={template.available ? 'default' : 'secondary'}
                        className={
                          template.available
                            ? 'bg-lime-green text-white hover:bg-lime-green/90'
                            : ''
                        }
                      >
                        {template.available
                          ? t('ADMIN_TEMPLATES_TABLE_BADGES_AVAILABLE')
                          : t('ADMIN_TEMPLATES_TABLE_BADGES_COMINGSOON')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {template.popular && (
                        <Badge variant="outline" className="border-selise-blue text-selise-blue">
                          {t('ADMIN_TEMPLATES_TABLE_BADGES_POPULAR')}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <code className="text-sm bg-muted px-2 py-1 rounded text-[hsl(var(--fg))]">
                        {template.organizationId ? 'Organization' : 'Global'}
                      </code>
                    </TableCell>
                    <TableCell className="text-sm text-globe-grey">
                      {template?.updatedAt
                        ? new Date(template.updatedAt).toLocaleDateString(locale)
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="ghost" size="icon-sm">
                              <MoreHorizontal className="size-4" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent align="end" className="w-44 p-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="w-full justify-start gap-2"
                              asChild
                            >
                              <Link to={`/admin/templates/${template.slug}/edit`}>
                                <Pencil className="size-4" />
                                {t('ADMIN_TEMPLATES_TABLE_ROWACTIONS_EDITDETAILS')}
                              </Link>
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="w-full justify-start gap-2"
                              asChild
                            >
                              <Link to={`/admin/templates/${template.slug}/builder`}>
                                <Settings2 className="size-4" />
                                {t('ADMIN_TEMPLATES_TABLE_ROWACTIONS_FORMBUILDER')}
                              </Link>
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="w-full justify-start gap-2"
                              onClick={async () => {
                                try {
                                  const data = await exportTemplate(template.slug);
                                  if (data) {
                                    downloadTemplateJson(data);
                                  }
                                } catch (err) {
                                  console.error('Export failed:', err);
                                }
                              }}
                            >
                              <Download className="size-4" />
                              Export
                            </Button>
                            <div className="h-px bg-border my-1" />
                            <Button
                              variant="ghost"
                              size="sm"
                              className="w-full justify-start gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                              onClick={() => onDelete(template)}
                            >
                              <Trash2 className="size-4" />
                              {t('ADMIN_TEMPLATES_TABLE_ROWACTIONS_DELETE')}
                            </Button>
                          </PopoverContent>
                        </Popover>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <TablePagination pagination={pagination} onPageChange={onPageChange} />
      )}
    </div>
  );
}
